"use client";

import { useState } from "react";
import { cropTextInMiddle, forgeNetworkURL, getNetworkName, formatBalance, getRewardChain } from "@cede/utils";
import { ClaimButton } from "./ClaimButton";
import { MerklRewardsSection } from "./MerklRewardsSection";
import { RewardCountdown } from "./RewardCountdown";
import { Alert } from "@/components/features/notifications/Alerts/Alert";
import { TokenIcon, BaseIcon } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { PortalModal } from "@/components/ui/portal-modal";
import { useDevice, RewardEpochDetails } from "@/hooks";
import { useFormatCurrency } from "@/hooks/currency";
import { clientConfig } from "@/lib/config/client-config";
import { cn } from "@/lib/utils";
import { getRewardChainLabel, getRewardNetworkCode } from "@/utils/rewards";
import Decimal from "decimal.js";
import { Info, ChevronDown, CircleOff, AlertTriangle } from "lucide-react";
import Image from "next/image";

type RewardsModalContentProps = {
  open: boolean;
  onClose: () => void;
  rewardDetails: {
    campaignData: {
      id: string;
      name: string;
      icon: string | null;
      title: string;
    };
    rewardDetails: (RewardEpochDetails & { epochLabel: string })[];
  }[];
  claimableSoonRewardDetails?: {
    campaignData: {
      id: string;
      name: string;
      icon: string | null;
      title: string;
    };
    rewardDetails: (RewardEpochDetails & { epochLabel: string })[];
  }[];
  /** Formatted vault rewards: what the footer claim pays, Merkl excluded. */
  vaultReward: string;
  /** USD total of vault (price-derived) and Merkl rewards. */
  totalRewardFiat: Decimal;
  /**
   * Chains the user has claimable rewards on, in claim order. Drives the
   * per-network sections of the Claim Details card. Empty array → falls back to
   * the default reward chain so the card is never blank.
   */
  rewardChainIds: number[];
  address: string;
  isLoading: boolean;
  onClaim: () => void;
  isTMA?: boolean;
  isClaimCooldownActive?: boolean;
  hasClaimableItems?: boolean;
  /** Merkl rewards are claimed from their own section, so the footer steps aside when they are all there is. */
  hasMerklClaimable?: boolean;
  batchProgress?: { current: number; total: number } | null;
  totalBatches?: number;
  /**
   * Per-chain claim progress (multi-chain claims only). When set, the footer
   * tells the user which network is being claimed and that a wallet network
   * switch is required before the next one.
   */
  chainClaimProgress?: {
    currentChainId: number;
    currentChainIndex: number;
    totalChains: number;
    completedChainIds: number[];
  } | null;
  cashbackSummary?: {
    claimableAmount: number;
    currency: string | null;
    onOpenCashback?: () => void;
  };
};

const CampaignAccordion = ({
  campaignData,
  rewardDetails,
  formatUsdtAsLocal,
  isUsd,
}: {
  campaignData: { id: string; name: string; icon: string | null; title: string };
  rewardDetails: (RewardEpochDetails & { epochLabel: string })[];
  formatUsdtAsLocal: (amount: number) => string;
  isUsd: boolean;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="rounded-lg border border-cede-border-container bg-cede-bg-component">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex w-full items-center justify-between p-3 transition-colors hover:bg-cede-bg-component/80"
      >
        <div className="flex items-center gap-2">
          <TokenIcon symbol={campaignData.name} iconUrl={campaignData.icon} size="md" />
          <span className="text-base font-medium text-cede-text-primary">{campaignData.title}</span>
        </div>
        <ChevronDown
          className={cn("h-5 w-5 text-cede-text-secondary transition-transform", isExpanded && "transform rotate-180")}
        />
      </button>
      {isExpanded && (
        <div className="border-t border-cede-border-container p-3 space-y-2">
          {rewardDetails.map((detail, index) => (
            <div key={`${campaignData.id}-${detail.epochId}-${index}`}>
              <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-4 py-2">
                <span className="text-sm text-cede-text-secondary">{detail.epochLabel}</span>
                <span
                  className={cn(
                    "text-sm text-cede-text-primary flex items-center gap-1",
                    detail.cooldownEnd ? "col-start-2" : "col-span-2 justify-end",
                  )}
                >
                  <TokenIcon symbol={detail.rewardTokenSymbol} size={14} />
                  {formatBalance(detail.amount, { roundForFiat: true })}
                  <span className="text-cede-text-secondary">{detail.rewardTokenSymbol}</span>
                  {/* Local-currency hint from the price-derived USD value; hidden when the
                      token is unpriced (amountFiat null) — never assume a USDT peg. */}
                  {!isUsd && detail.amountFiat != null && detail.amountFiat > 0 && (
                    <span className="text-cede-text-secondary">({formatUsdtAsLocal(detail.amountFiat)})</span>
                  )}
                </span>
                {detail.cooldownEnd && <RewardCountdown cooldownDate={new Date(detail.cooldownEnd)} />}
              </div>
              {index < rewardDetails.length - 1 && <div className="h-px bg-cede-border-container" />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

type CampaignBlock = {
  campaignData: { id: string; name: string; icon: string | null; title: string };
  rewardDetails: (RewardEpochDetails & { epochLabel: string })[];
};

/**
 * Groups campaign blocks by the chain their rewards live on, preserving order.
 * A campaign's epochs share a chain, so the first reward detail's `chainId`
 * labels the block; blocks without a chainId collapse into one trailing group.
 */
function groupCampaignsByChain(
  campaigns: CampaignBlock[],
): { chainId: number | undefined; campaigns: CampaignBlock[] }[] {
  const groups = new Map<number | undefined, CampaignBlock[]>();
  for (const campaign of campaigns) {
    const chainId = campaign.rewardDetails[0]?.chainId;
    const existing = groups.get(chainId);
    if (existing) {
      existing.push(campaign);
    } else {
      groups.set(chainId, [campaign]);
    }
  }
  return [...groups.entries()].map(([chainId, blocks]) => ({ chainId, campaigns: blocks }));
}

const RewardsSection = ({
  title,
  rewardDetails,
  formatUsdtAsLocal,
  isUsd,
}: {
  title: string;
  rewardDetails: CampaignBlock[];
  formatUsdtAsLocal: (amount: number) => string;
  isUsd: boolean;
}) => {
  // Group campaign blocks by the chain their rewards are distributed on. A
  // campaign's epochs share one chain, so the first detail's chainId labels the
  // whole block. `undefined` chainId (claimable-soon, or pre-rollout claims)
  // collapses into a single unlabeled group.
  const chainGroups = groupCampaignsByChain(rewardDetails);
  const showChainHeadings = chainGroups.length > 1;

  return (
    <div className="rounded-2xl bg-cede-bg-component/50 border border-cede-border-container p-5">
      <h3 className="text-lg font-semibold text-cede-text-primary mb-3">{title}</h3>
      <div className="space-y-2">
        {rewardDetails.length === 0 ? (
          <div className="flex items-center gap-2 rounded-lg border border-dashed border-cede-border-container bg-cede-bg-component/30 px-3 py-4 text-cede-text-secondary">
            <CircleOff className="h-5 w-5" />
            <span className="text-sm italic">No rewards available</span>
          </div>
        ) : (
          chainGroups.map((group) => (
            <div key={group.chainId ?? "unknown-chain"} className="space-y-2">
              {showChainHeadings && group.chainId != null && (
                <p className="text-xs font-medium uppercase tracking-wide text-cede-text-secondary px-1">
                  {getRewardChainLabel(group.chainId)}
                </p>
              )}
              {group.campaigns.map((detail) => (
                <CampaignAccordion
                  key={detail.campaignData.id}
                  campaignData={detail.campaignData}
                  rewardDetails={detail.rewardDetails}
                  formatUsdtAsLocal={formatUsdtAsLocal}
                  isUsd={isUsd}
                />
              ))}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export const RewardsModalContent = ({
  open,
  onClose,
  rewardDetails,
  claimableSoonRewardDetails,
  vaultReward,
  totalRewardFiat,
  rewardChainIds,
  address,
  isLoading,
  onClaim,
  isTMA = false,
  isClaimCooldownActive = false,
  hasClaimableItems = false,
  hasMerklClaimable = false,
  batchProgress = null,
  totalBatches = 1,
  chainClaimProgress = null,
  cashbackSummary,
}: RewardsModalContentProps) => {
  const { isMobile } = useDevice();
  const { formatUsdtAsLocal, isUsd } = useFormatCurrency();

  // Chains to show in the Claim Details card. Fall back to the default reward
  // chain when none are known yet so the network card is never blank.
  const claimChainIds = rewardChainIds.length > 0 ? rewardChainIds : [getRewardChain(clientConfig.nodeEnv).id];

  return (
    <PortalModal open={open} onOpenChange={onClose} className="w-full md:w-[60vw] lg:w-[45vw] max-h-[90vh]">
      <PortalModal.Header>
        <PortalModal.Title className="text-2xl font-bold text-center bg-gradient-to-r from-cede-primary to-cede-primary/80 bg-clip-text text-transparent">
          {isTMA ? "Rewards Information" : "Claim Rewards"}
        </PortalModal.Title>
      </PortalModal.Header>

      {/* Scrollable Content */}
      <PortalModal.Content scrollable className="pb-6 mb-2 gap-6 border-b border-cede-border-container">
        {isTMA ? (
          // TMA-specific content
          <div className="rounded-2xl bg-cede-bg-component/50 border border-cede-border-container p-5 text-center">
            <h3 className="text-lg font-semibold text-cede-text-primary mb-2">Rewards Distribution</h3>
            <p className="text-sm text-cede-text-secondary">
              Your rewards will be automatically distributed to your connected TON wallets by our team. No manual
              claiming is required. Please check your wallet periodically for reward distributions.
            </p>
          </div>
        ) : (
          <>
            {/* Total Reward Card — fiat-denominated sum across all reward tokens, Merkl included */}
            <div className="rounded-2xl bg-cede-bg-component/50 border border-cede-border-container p-5 text-center">
              <p className="text-sm text-cede-text-secondary mb-2">Total Reward Amount</p>
              <p className="text-4xl font-bold text-cede-text-primary flex items-center justify-center gap-2 mb-1">
                ${formatBalance(totalRewardFiat, { roundForFiat: true })}
              </p>
              {!isUsd && totalRewardFiat.gt(0) && (
                <p className="text-xl text-cede-text-secondary">({formatUsdtAsLocal(totalRewardFiat.toNumber())})</p>
              )}
            </div>

            {cashbackSummary && (
              <div className="rounded-2xl bg-cede-bg-component/50 border border-cede-border-container p-5">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm text-cede-text-secondary">Cashback</p>
                    <div className="text-lg font-semibold text-cede-text-primary">
                      {cashbackSummary.claimableAmount > 0 ? (
                        <span className="flex items-center gap-1">
                          <TokenIcon symbol={cashbackSummary.currency ?? "USDT"} size={18} />
                          {formatBalance(cashbackSummary.claimableAmount, { roundForFiat: true })}
                          {!isUsd && (
                            <span className="text-sm text-cede-text-secondary">
                              ({formatUsdtAsLocal(cashbackSummary.claimableAmount)})
                            </span>
                          )}
                        </span>
                      ) : (
                        "No claimable cashback"
                      )}
                    </div>
                  </div>
                  {cashbackSummary.onOpenCashback && (
                    <Button variant="secondary" size="small" onClick={cashbackSummary.onOpenCashback}>
                      View Cashback
                    </Button>
                  )}
                </div>
              </div>
            )}

            {/* Information Alert */}
            <Alert
              variant="info"
              icon={<Info className="h-5 w-5" />}
              content={
                <div className="flex flex-col gap-2 text-left text-sm text-cede-text-secondary">
                  <p>
                    Rewards are locked for 24 hours after the end of the epoch. You can claim them anytime after the
                    locking period.
                  </p>
                  <p>
                    Check <span className="font-bold">Claimable Soon</span> section to see when you can claim your
                    rewards.
                  </p>
                </div>
              }
            />

            {/* Available Campaigns Section */}
            <RewardsSection
              title="Available Campaigns"
              rewardDetails={rewardDetails}
              formatUsdtAsLocal={formatUsdtAsLocal}
              isUsd={isUsd}
            />

            {/* Claimable Soon Section */}
            {claimableSoonRewardDetails !== undefined && (
              <RewardsSection
                title="Claimable Soon"
                rewardDetails={claimableSoonRewardDetails}
                formatUsdtAsLocal={formatUsdtAsLocal}
                isUsd={isUsd}
              />
            )}

            <MerklRewardsSection />

            {/* Network and Wallet Info */}
            <div className="rounded-2xl bg-cede-bg-component/50 border border-cede-border-container p-5">
              <h3 className="text-lg font-semibold text-cede-text-primary mb-4">Claim Details</h3>
              <div className="space-y-4">
                {/* Networks Card — one row per chain the user claims on. */}
                <div className="rounded-lg border border-cede-border-container bg-cede-bg-component/50 p-3">
                  <p className="text-xs font-medium text-cede-text-secondary uppercase mb-2">
                    {claimChainIds.length > 1 ? "Networks" : "Network"}
                  </p>
                  <div className="space-y-2">
                    {claimChainIds.map((chainId) => {
                      const networkCode = getRewardNetworkCode(chainId);
                      return (
                        <div key={chainId} className="flex items-center gap-2.5">
                          <BaseIcon size="md">
                            <Image
                              src={forgeNetworkURL(networkCode)}
                              alt={getRewardChainLabel(chainId)}
                              width={24}
                              height={24}
                            />
                          </BaseIcon>
                          <span className="text-base font-semibold text-cede-text-primary">
                            {networkCode ? getNetworkName(networkCode) : getRewardChainLabel(chainId)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                  {claimChainIds.length > 1 && (
                    <p className="text-xs text-cede-text-secondary mt-2">
                      You&apos;ll claim on each network in turn — confirm a network switch when prompted.
                    </p>
                  )}
                </div>
                {/* Wallet Address Card */}
                <div className="rounded-lg border border-cede-border-container bg-cede-bg-component/50 p-3">
                  <p className="text-xs font-medium text-cede-text-secondary uppercase mb-2">Wallet Address</p>
                  <span className="text-sm font-mono text-cede-text-primary break-all leading-relaxed">
                    {isMobile ? cropTextInMiddle(address, 15) : address}
                  </span>
                </div>
              </div>
            </div>
          </>
        )}
      </PortalModal.Content>

      {/* Fixed Action Button at Bottom */}
      <PortalModal.Footer>
        {isTMA ? (
          <Button variant="primary" size="large" onClick={onClose} className="w-full">
            Close
          </Button>
        ) : (
          <div className="flex w-full flex-col gap-2">
            {/* One adaptive banner: live per-chain progress while claiming, otherwise a
                single upfront note covering both the transaction count and (when
                multi-chain) the network switches. */}
            {(chainClaimProgress?.totalChains ?? 0) > 1 ? (
              <Alert
                variant="warning"
                icon={<AlertTriangle className="h-5 w-5" />}
                content={`Claiming on ${getRewardChainLabel(chainClaimProgress!.currentChainId)} (network ${chainClaimProgress!.currentChainIndex} of ${chainClaimProgress!.totalChains})${
                  batchProgress ? ` — transaction ${batchProgress.current} of ${batchProgress.total}` : ""
                }. Approve the network switch in your wallet when prompted to claim on the next chain.`}
              />
            ) : claimChainIds.length > 1 ? (
              <Alert
                variant="info"
                icon={<Info className="h-5 w-5" />}
                content={`Your rewards span ${claimChainIds.length} networks: you'll sign ${totalBatches} transactions and approve a network switch in your wallet between them.`}
              />
            ) : (
              (batchProgress?.total ?? totalBatches) > 1 && (
                <Alert
                  variant="warning"
                  icon={<AlertTriangle className="h-5 w-5" />}
                  content={
                    batchProgress
                      ? `Transaction ${batchProgress.current} of ${batchProgress.total}...`
                      : `To receive all your rewards you will have to accept ${totalBatches} transactions in a row`
                  }
                />
              )
            )}
            {(hasClaimableItems || !hasMerklClaimable) && (
              <ClaimButton
                text="Claim Rewards"
                reward={vaultReward}
                disabledText="Claim Not Available"
                variant={isLoading ? "loading" : isClaimCooldownActive || !hasClaimableItems ? "disabled" : "simple"}
                onClick={onClaim}
                fullWidth
                showIcon={false}
              />
            )}
          </div>
        )}
      </PortalModal.Footer>
    </PortalModal>
  );
};
